import { Typography, IconButton, Tooltip, Stack, Box } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

import ContainerTitle from '../../../components/ui/container-title';
import { getColor } from '../../../assets/theme';
import RoomPostingDataTable from '../components/room-posting/fo-roomposting-table';
import RoomPostingEntry from '../components/room-posting/fo-roomposting-entry';
import {
  CustomError,
  fetchHandleError,
} from '../../../utils/api-error-handling';

import {
  useEffect,
  useState,
  createContext,
  useContext,
  memo,
  useRef,
  useMemo,
} from 'react';
import { Close } from '@mui/icons-material';
import ServerError from '../../../components/animation/server-error';
import { useGetLedgerListQuery } from '../../../api/front-office/roomposting.api.rtk';
import { useDeleteNetLockMutation } from '../../../api/front-office/netlock.api.rtk';

import {
  RoomPostingLedgerList,
  RoomPostingLedgerInit,
  RoomPostingTransactionDetails,
  RoomPostingDataContext,
  RoomPostingCLPaymentBreakdownEntry,
} from '../interface/room-posting-interface';

import { useAppSelector } from '../../../store';

import SelectProperty from '../../../components/animation/select-property';
import { GridRowSelectionModel } from '@mui/x-data-grid';
// Create useContext RoomPostingDataContext for global without using props
const FolioUUIDContext = createContext<RoomPostingDataContext | undefined>(
  undefined,
);
import useToast from '../../../hooks/useToast';
import { ModalWrapper } from '../../../hooks/useModal';
import useDriver from '@hooks/useDriver';
import {
  addFAQButton,
  simulateClick,
  simulateContextMenu,
  simulateDoubleClick,
  simulateMouseOut,
  simulateMouseOver,
} from '@utils/guide';

// Custom hook to consume the context
export const useRoomPostingContext = () => {
  const context = useContext(FolioUUIDContext);
  if (!context) {
    throw new CustomError(fetchHandleError('No Folio'));
  }
  return context;
};

const FORoomPostingContainer = ({
  transactionDetails,
  isOpenModal = true,
  onCloseModal = () => false,
  transactionType = 'Room Posting',
  isCityLedgerPosting = false,
  isSpecificBooking,
  resvDrawerOnClose,
}: {
  transactionDetails: RoomPostingTransactionDetails;
  isOpenModal?: boolean;
  onCloseModal?: (forceClose: boolean, refetchReservation?: boolean) => void;
  transactionType?: string;
  isCityLedgerPosting?: boolean;
  isSpecificBooking?: boolean;
  resvDrawerOnClose?: () => void;
}) => {
  //Net Lock
  const { toast } = useToast();

  const [deleteNetLock] = useDeleteNetLockMutation();
  const [netLock, setNetLock] = useState<boolean>(false);

  //Modal Status
  const [isOpen, setisOpen] = useState(isOpenModal);
  // console.log('isOpenModal: ', isOpenModal);
  useEffect(() => {
    setisOpen(isOpenModal);
  }, [isOpenModal]);

  //Get Ledger List and Entries List Initial Data
  const [roomPostingLedgerList, setRoomPostingLedgerList] =
    useState<RoomPostingLedgerList>(RoomPostingLedgerInit);

  //Select Folio UUID
  const [selectedFolioUuid, setSelectedFolioUuid] = useState<string>(
    transactionDetails?.folioUuid,
  );
  const [selectedFolioStatus, setSelectedFolioStatus] = useState<string>(
    transactionDetails?.folioStatus,
  );

  const [clEditedRows, setCLEditedRows] = useState<
    RoomPostingCLPaymentBreakdownEntry[]
  >([]);

  const srcNav =
    transactionDetails?.folioUuid?.length > 0 ? 'Other Forms' : 'Room Posting';
  useEffect(() => {
    setSelectedFolioUuid(transactionDetails.folioUuid);
    setSelectedFolioStatus(transactionDetails.folioStatus);
    setRoomPostingLedgerList(RoomPostingLedgerInit);
    setSelectedRoomNo('');
    setSelectedFolioNo('');
    setSelectedGuestName('');
  }, [transactionDetails.folioUuid, transactionDetails.folioStatus]);
  //Folio Header Details
  const [selectedGuestName, setSelectedGuestName] = useState('');
  const [selectedFolioNo, setSelectedFolioNo] = useState('');
  const [selectedBookingID, setSelectedBookingID] = useState('');
  const [selectedRoomNo, setSelectedRoomNo] = useState('');
  const [selectedSubGroup, setSelectedSubGroup] = useState(0);
  const [refetchList, setRefetchList] = useState(false);
  const [subGroupValue, setSubGroupValue] = useState('');
  // Checkbox state to show All City Ledger Transactions
  const [isShowCLTrans, setIsShowCLTrans] = useState(false);
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsShowCLTrans(event.target.checked);
  };
  // API fetching for Folio Details
  const {
    data: ledgerResult = [],
    refetch: refetchLedger,
    isError,
    isFetching,
    isLoading,
  } = useGetLedgerListQuery(
    {
      transactionIdentifier:
        selectedFolioUuid ?? (transactionDetails?.folioUuid || 'Nan'),
      transactionType: transactionType,
      isCityLedgerPosting: isCityLedgerPosting,
      showIsClosed: isShowCLTrans,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !isOpen || (selectedFolioUuid.length === 0 && !selectedRoomNo),
    },
  );
  // Wrap the refetch function to detect when it's called
  const ledgerRefetch = async () => {
    setRefetchList(true);
    await refetchLedger(); // Call the original refetch function
  };

  // Use Effect Hooks
  //Ledger List, Summary, Balance and Entries
  // const memoizedLedgerResult = useMemo(() => ledgerResult, [ledgerResult]);
  useEffect(() => {
    if (!isOpen || (!selectedFolioUuid && !selectedRoomNo)) return;

    setRoomPostingLedgerList(ledgerResult);
    setSelectedRoomNo(ledgerResult?.data?.roomNo);
    setSelectedFolioNo(ledgerResult?.data?.folioNo);
    setSelectedBookingID(ledgerResult?.data?.ledgerResult?.[0]?.booking_id);
    setSelectedGuestName(ledgerResult?.data?.guestName);
    setNetLock(roomPostingLedgerList.data?.netLock !== 0);
  }, [isOpen, selectedFolioUuid, ledgerResult]);
  useEffect(() => {
    if (netLock) {
      toast({
        severity: 'warning',
        message: `${
          roomPostingLedgerList.data?.lockByUserShortName || 'Someone'
        } is transacting on Room Posting.`,
      });
    }
    setNetLock(false);
  }, [ledgerResult, netLock]);
  //close modal
  const handleCloseClick = () => {
    setisOpen(false);
    const folioUuid = roomPostingLedgerList?.data?.folioUuid;

    if (folioUuid) {
      deleteNetLock(`${folioUuid}/Booking`)
        .unwrap()
        .catch(() =>
          toast({
            severity: 'error',
            message: 'Error deleting net lock on database.',
          }),
        );
    }

    onCloseModal?.(false, refetchList);
    setRefetchList(false);
    setNetLock(false);
  };
  //colors
  const { primary, borderColor, white } = getColor();
  const Property = useAppSelector((state) => state.properties.selectedProperty);
  const useProperty = Property.value;
  const [selectedLedgers, setSelectedLedgers] = useState<GridRowSelectionModel>(
    [],
  );
  const voidRef = useRef<(() => void) | null>(null);

  // #region GUIDE
  const showGuide = useMemo(
    () => ledgerResult?.data?.ledgerResult?.length > 0,
    [ledgerResult],
  );

  const { driver, setOnGuide, defaultConfig } = useDriver();

  const handleDefaultGuide = () => {
    const driverObj = driver({
      ...defaultConfig,
      steps: [
        {
          element: '#room-number',
          popover: {
            title: 'Room Posting',
            description:
              '<b>Enter the Room Number</b> and Date you want to post charges for. This will retrieve associated charges, payments, and collections associated with the room.',
            onPopoverRender: (popover) =>
              addFAQButton({
                popover,
                link: 'https://servoitsolutions.ph/support/kb/faq.php?id=1962',
                driverInstance: driverObj,
              }),
          },
        },
      ],
      onDestroyed: () => setOnGuide(false),
      onHighlightStarted: () => setOnGuide(true),
    });

    driverObj.drive();
  };

  const handleGuide = () => {
    const driverObj = driver({
      ...defaultConfig,
      steps: [
        {
          element: '.room-posting-datatable .MuiDataGrid-row',
          popover: {
            title: 'Room Posting',
            description: `
              <div style="max-height: 200px; overflow:auto;">
              The Room Posting feature on the Xenia Cloud Property Management System allows you to efficiently manage and post charges and collections for guest rooms. This guide provides a step-by-step walkthrough on how to use this feature, from posting charges and applying discounts to transferring entries and processing checkouts.

              </br></br>There are three ways you can get to the Room Posting window: 
              <ul>
                <li>Through the Main navigation bar > Room Posting</li>
                <li>Through Calendar > Room Plan </li>
                <li>Through the Booking window</li>
              </ul>
              </br>Tick the entry, then <b>right-click</b> to open the following options. The following images are previews of some of these features.
              </div>
              `,
            onNextClick: () => {
              simulateClick('[aria-label="Select row"]');
              simulateContextMenu('.room-posting-datatable .MuiDataGrid-row');

              setTimeout(() => {
                driverObj.moveNext();
              }, 100);
            },
          },
        },
        {
          element: '#room-posting-menu ul',
          popover: {
            title: 'Room Posting',
            description: `
            <ul>
              <li><b>Apply Discount –</b> Allows you to apply discounts (regular discounts, senior / PWD) </li>
              <li><b>Modify Line –</b> Customize service charges, apply Zero-Rated VAT, or Remove Discounts. </li>
              <li><b>Line Transfer –</b> Transfer charges to a different room. </li>
              <li><b>Void –</b> Void entries.</li>
              <li><b>Transfer To –</b> Allows you to transfer the entry to the default or different folios.</li>
            </ul>`,
            onNextClick: () => {
              simulateClick('#apply-discount');

              setTimeout(() => {
                driverObj.moveNext();
              }, 100);
            },
            onPrevClick: () => {
              simulateContextMenu('#apply-discount');

              setTimeout(() => {
                driverObj.movePrevious();
              }, 100);
            },
          },
        },
        {
          element: '#room-posting-table-modal .MuiBox-root .MuiBox-root',
          popover: {
            title: 'Room Posting',
            description: 'Apply Discounts Modal',
            onNextClick: () => {
              simulateClick('#modal-close-btn');

              simulateContextMenu('.room-posting-datatable .MuiDataGrid-row');

              setTimeout(() => {
                simulateClick('#modify-line');
              }, 100);

              setTimeout(() => {
                driverObj.moveNext();
              }, 200);
            },

            onPrevClick: () => {
              simulateContextMenu('.room-posting-datatable .MuiDataGrid-row');
              simulateClick('#modal-close-btn');

              setTimeout(() => {
                driverObj.movePrevious();
              }, 100);
            },
          },
        },
        {
          element: '#room-posting-table-modal .MuiBox-root .MuiBox-root',
          popover: {
            title: 'Room Posting',
            description: 'Modify Line Modal',
            onNextClick: () => {
              simulateClick('#modal-close-btn');

              setTimeout(() => {
                driverObj.moveNext();
              }, 100);
            },

            onPrevClick: () => {
              simulateClick('#modal-close-btn');

              setTimeout(() => {
                simulateContextMenu('.room-posting-datatable .MuiDataGrid-row');
              }, 100);

              setTimeout(() => {
                simulateClick('#apply-discount');
              }, 200);

              setTimeout(() => {
                driverObj.movePrevious();
              }, 300);
            },
          },
        },
        {
          element: '[data-field="line_remarks"][data-colindex="6"]',
          popover: {
            title: 'Room Posting',
            description:
              'You can <b>double-click</b> the selected entry to add or edit <b>Remarks</b> for the entry.',
            onNextClick: () => {
              simulateClick('#modal-close-btn');

              setTimeout(() => {
                simulateDoubleClick(
                  '[data-field="line_remarks"][data-colindex="6"]',
                );
              }, 100);

              setTimeout(() => {
                driverObj.moveNext();
              }, 200);
            },

            onPrevClick: () => {
              simulateContextMenu('.room-posting-datatable .MuiDataGrid-row');

              setTimeout(() => {
                simulateClick('#modify-line');
              }, 100);

              setTimeout(() => {
                driverObj.movePrevious();
              }, 200);
            },
          },
        },
        {
          element: '#room-posting-table-modal .MuiBox-root .MuiBox-root',
          popover: {
            title: 'Room Posting',
            description: 'Item Entry Remarks Modal',
            onNextClick: () => {
              simulateClick('#item-remarks-cancel');

              setTimeout(() => {
                driverObj.moveNext();
              }, 100);
            },
            onPrevClick: () => {
              simulateClick('#item-remarks-cancel');

              setTimeout(() => {
                driverObj.movePrevious();
              }, 100);
            },
          },
        },
        {
          element: '#view-detailed-folio',
          popover: {
            title: 'Room Posting',
            description:
              '<b>Click View Detailed Folio</b> to view more details such as local taxes, service charges, net, outlet, cashier, voided, and other columns. <b>Drag the horizontal</b> scrollbar to the right to explore these columns.',
            onPrevClick: () => {
              simulateDoubleClick(
                '[data-field="line_remarks"][data-colindex="6"]',
              );

              setTimeout(() => {
                driverObj.movePrevious();
              }, 100);
            },
          },
        },
        {
          element: '#room-posting-form',
          popover: {
            title: 'Room Posting',
            description:
              'At the bottom of the window, you can post charges and specify the entry type, amount and remarks. After inputting the information required, <b>click</b> Post.',
          },
        },
        {
          element: '#room-posting_checkout-more',
          popover: {
            title: 'Room Posting',
            description:
              'To perform a regular Check Out, simply <b>click the Check Out</b> button. By clicking the drop-down button next to <b>Check Out,</b> you can check out the guest via City Ledger, mark the check out as <b>pending,</b> or complete the checkout while sending the folio to their email address.Note that guests cannot be checked out if they have unsettled charges.',
            onNextClick: () => {
              simulateClick('#room-posting_checkout-more');

              setTimeout(() => {
                driverObj.moveNext();
              }, 200);
            },
          },
        },
        {
          element: '#checkout-menu ul',
          popover: {
            title: 'Room Posting',
            description: 'Menu Items',
            onNextClick: () => {
              simulateClick('#checkout-menu .MuiBackdrop-root');

              setTimeout(() => {
                driverObj.moveNext();
              }, 200);
            },
            onPrevClick: () => {
              simulateClick('#checkout-menu .MuiBackdrop-root');

              setTimeout(() => {
                driverObj.movePrevious();
              }, 100);
            },
          },
        },
        {
          element: '#view',
          popover: {
            title: 'Room Posting',
            description:
              '<b>Click the View drop-down</b> to filter by folio, charges, or payments.',
            onPrevClick: () => {
              simulateClick('#room-posting_checkout-more');

              setTimeout(() => {
                driverObj.movePrevious();
              }, 100);
            },
          },
        },
        {
          element: '#search',
          popover: {
            title: 'Room Posting',
            description:
              'If you want to locate a specific posting, <b>input the Item/Service or Remarks</b> on the <b>Search</b> bar.',
          },
        },
        {
          element: '[data-field="charges"][data-colindex="3"]',
          popover: {
            title: 'Room Posting',
            description:
              'You may hover your mouse over the charges, collections, or adjustments column to view its breakdown.',
            onPrevClick: () => {
              simulateMouseOut('[data-field="charges"][data-colindex="3"] p');

              setTimeout(() => {
                driverObj.movePrevious();
              }, 100);
            },
          },
          onHighlightStarted: () =>
            simulateMouseOver('[data-field="charges"][data-colindex="3"] p'),
        },
        {
          element: '#custom-tooltip',
          popover: {
            title: 'Room Posting',
            description: 'Adjustments Breakdown.',
            onPopoverRender: (popover) =>
              addFAQButton({
                popover,
                link: 'https://servoitsolutions.ph/support/kb/faq.php?id=1962',
                driverInstance: driverObj,
              }),
          },
        },
      ],
      onDestroyed: () => setOnGuide(false),
      onHighlightStarted: () => {
        setOnGuide(true);
      },
    });

    driverObj.drive();
  };

  // #endregion

  return (
    <FolioUUIDContext.Provider
      value={{
        selectedFolioUuid,
        selectedFolioStatus,
        setSelectedFolioUuid,
        roomPostingLedgerList,
        setRoomPostingLedgerList,
        selectedSubGroup,
        setSelectedSubGroup,
        useProperty,
        ledgerRefetch,
        srcNav,
        transactionType,
        setSelectedLedgers,
        selectedLedgers,
        setCLEditedRows,
        clEditedRows,
        isOpen,
      }}
    >
      <>
        {useProperty === 'All Properties' && isOpen ? (
          <SelectProperty />
        ) : (
          <ModalWrapper
            id='room-posting-modal'
            disableOnClose
            onClose={handleCloseClick}
            modalDisclosure={{
              isOpen: isOpen,
              onClose: handleCloseClick,
              onToggle: () => setisOpen(!isOpen),
              onOpen: () => setisOpen(true),
            }}
          >
            <Stack sx={{ overflow: 'auto', maxHeight: '90dvh' }}>
              {/* HEADER */}
              <Stack
                direction='row'
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 20,
                  bgcolor: white,
                  justifyContent: 'space-between',
                  p: { xs: 2, lg: 3 },
                  borderBottom: `1px solid ${borderColor}`,
                  borderTopRightRadius: '8px',
                  borderTopLeftRadius: '8px',
                }}
              >
                <Stack
                  sx={{ gap: '10px' }}
                  direction={{ xs: 'column', lg: 'row' }}
                >
                  <ContainerTitle
                    title={transactionType}
                    {...(showGuide && !isLoading && !isFetching
                      ? { func: handleGuide }
                      : !showGuide &&
                        !isLoading &&
                        !isFetching && { func: handleDefaultGuide })}
                  />
                  {!isError && selectedGuestName && (
                    <Typography
                      sx={[
                        {
                          pt: '4px',
                          fontSize: '14px!important',
                          fontWeight: 600,
                          letterSpacing: '.15px',
                          lineHeight: '160%',
                          color: '#00b8d9',
                        },
                      ]}
                      variant='h6'
                    >
                      <Tooltip
                        title={`Folio No. ${selectedFolioNo || 'N/A'}`}
                        placement='right-end'
                      >
                        <IconButton
                          sx={{
                            border: 0,
                            color: '#00b8d9',
                            bgcolor: 'transparent',
                            ':hover': {
                              color: primary,
                              bgcolor: 'transparent',
                            },
                          }}
                          disableRipple
                        >
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>

                      {`Reservation ID:${selectedBookingID || 'N/A'} Room: ${selectedRoomNo || 'N/A'} • ${
                        selectedGuestName || 'N/A'
                      } • Folio: ${selectedFolioNo || 'N/A'}`}
                    </Typography>
                  )}
                </Stack>
                <IconButton
                  id='modal-close-btn'
                  disableRipple
                  onClick={handleCloseClick}
                  sx={{
                    position: { xs: 'absolute', lg: 'relative' },
                    right: '10px',
                    ':hover': {
                      color: primary,
                    },
                  }}
                >
                  <Close />
                </IconButton>
              </Stack>

              {/* MAIN */}
              {isError ? (
                <Stack justifyContent='center' alignItems='center'>
                  <ServerError
                    sx={{
                      position: 'unset',
                      transform: 'unset',
                    }}
                  />
                </Stack>
              ) : (
                <Stack direction={{ xs: 'column', sm: 'row' }} gap='10px'>
                  <Box sx={{ width: '100%' }}>
                    <RoomPostingDataTable
                      setVoid={(func) => (voidRef.current = func)}
                      isNetLocked={netLock}
                      isFetchingLedger={isFetching || isLoading}
                      isCityLedgerPosting={isCityLedgerPosting}
                      isShowCLTrans={isShowCLTrans}
                      onCheckboxChange={handleCheckboxChange}
                      bookingStatus={selectedFolioStatus || ''}
                      isSpecificBooking={isSpecificBooking}
                      subGroupValue={subGroupValue}
                      setSubGroupValue={setSubGroupValue}
                    />
                    <Box
                      sx={{
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 20,
                        bgcolor: white,
                        width: '100%',
                        borderRadius: '4px',
                      }}
                    >
                      <RoomPostingEntry
                        onCloseForm={() => {
                          handleCloseClick();
                        }}
                        handleVoidItem={() =>
                          voidRef.current && voidRef.current()
                        }
                        isNetLocked={netLock}
                        isCityLedgerPosting={isCityLedgerPosting}
                        selectedFolioNo={selectedFolioNo}
                        selectedGuestName={selectedGuestName}
                        isSpecificBooking={isSpecificBooking}
                        subGroupValue={subGroupValue}
                        selectedRoomStatus={transactionDetails?.roomStatus}
                        handleCloseResvDrawer={resvDrawerOnClose}
                      />
                    </Box>
                  </Box>
                </Stack>
              )}
            </Stack>
          </ModalWrapper>
        )}
      </>
    </FolioUUIDContext.Provider>
  );
};

export default memo(FORoomPostingContainer);
